import { Fragment, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  useFetchTestimonials,
  useGetSocialProofAssetById,
} from "../api/queries";
import Masonry from "react-masonry-css";
import { GenericTestimonialCard } from "@/components/testify/card";
import { VariantId } from "@/components/testify/card/presets";
import { MasonryGalleryCustomProps } from "../api/types";
import { WidgetComponent } from "@/types";
import { LoadFonts } from "@/components/atoms/load-fonts";

export const VISIBLE_TESTIMONIALS = 10;

export interface MasonryGalleryProps {
  projectId: string;
  variant: VariantId;
  properties: MasonryGalleryCustomProps;
}

export function MasonryGallery({
  variant,
  projectId,
  properties,
}: MasonryGalleryProps) {
  const { data: testimonials, isPending } = useFetchTestimonials({
    projectId,
    published: true,
    type: !properties.visibilityOptions?.showAssets ? "text" : undefined,
  });
  const [visibleTestimonials, setVisibleTestimonials] =
    useState(VISIBLE_TESTIMONIALS);

  const loadMore = () => {
    setVisibleTestimonials((prevVisible) => prevVisible + 3);
  };

  const breakpointColumnsObj = {
    default: Number(properties.columnCount),
    1100: 2,
    700: 1,
  };

  return (
    <Fragment>
      {isPending ? (
        <div></div>
      ) : (
        <div
          className="testify-w-full testify-container testify-mx-auto testify-px-4 testify-py-8"
          style={{ fontFamily: properties.font || "Roboto" }}
        >
          <LoadFonts font={properties.font || "Roboto"} />
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="testify-flex testify-w-auto testify--ml-4"
            columnClassName="testify-pl-5 testify-bg-clip-padding"
          >
            {testimonials?.slice(0, visibleTestimonials).map((testimonial) => (
              <div className="testify-my-5">
                <GenericTestimonialCard
                  isUsedInParent
                  data={testimonial}
                  variant={variant}
                  properties={properties}
                />
              </div>
            ))}
          </Masonry>
          {visibleTestimonials < (testimonials?.length || 0) && (
            <div className="testify-text-center testify-mt-8">
              <Button onClick={loadMore} variant="outline">
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </Fragment>
  );
}

export const MasonryGalleryComponentWidget: WidgetComponent = ({
  widgetId,
}) => {
  const { data, isPending } =
    useGetSocialProofAssetById<MasonryGalleryCustomProps>({ id: widgetId });

  if (!data || isPending) return;

  return (
    <main className="testify-w-full">
      <LoadFonts font={data.properties.font} />
      <section className="testify-pb-4">
        <MasonryGallery
          projectId={data.projectId!}
          properties={data.properties}
          variant={data.properties.cardVariant}
        />
      </section>
    </main>
  );
};
